import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, CheckCircle, XCircle, Clock, User, Calendar, MessageSquare, Paperclip, Download, Image, File } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import ticketService, { TicketStatus, TicketPriority } from '../../services/ticketService';
import { TicketStatusPersian, TicketPriorityPersian, TicketStatusBadge, TicketPriorityBadge } from '../../constants/ticketEnums';
import { getFullImageUrl } from '../../utils/imageUpload';
import toast from 'react-hot-toast';

const TicketDetailContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 10px 15px;
  color: #b8b8d1;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: #2d2d44;
    color: #00d4ff;
    border-color: #00d4ff;
  }
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ConversationArea = styled(Card)`
  min-height: 600px;
`;

const ConversationHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ConversationTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MessagesContainer = styled.div`
  height: 500px;
  overflow-y: auto;
  padding: 20px;
  direction: rtl;
`;

const MessageContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  justify-content: ${props => props.isAdmin ? 'flex-start' : 'flex-end'};
`;

const Message = styled.div`
  max-width: 80%;
  padding: 15px 20px;
  border-radius: 12px;
  position: relative;
  background: ${props => props.isAdmin 
    ? 'linear-gradient(135deg, #e7f1ff 0%, #b3d7ff 100%)' 
    : 'linear-gradient(135deg, #f8f9fa 0%, #dee2e6 100%)'
  };
  border: 1px solid ${props => props.isAdmin ? '#b3d7ff' : '#dee2e6'};
  color: #212529;
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
`;

const MessageAuthor = styled.strong`
  color: #495057;
`;

const MessageTime = styled.small`
  color: #6c757d;
`;

const MessageContent = styled.div`
  line-height: 1.6;
  
  h6 {
    margin-bottom: 8px;
    color: #495057;
    font-size: 16px;
  }
  
  p {
    margin: 0;
    color: #212529;
  }
`;

const AttachmentContainer = styled.div`
  margin-top: 15px;
  padding: 10px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AttachmentIcon = styled.div`
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AttachmentInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const AttachmentName = styled.div`
  color: #495057;
  font-size: 14px;
  font-weight: 500;
`;

const AttachmentSize = styled.div`
  color: #6c757d;
  font-size: 12px;
`;

const AttachmentActions = styled.div`
  display: flex;
  gap: 8px;
`;

const AttachmentButton = styled.button`
  background: none;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 6px 10px;
  color: #495057;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  
  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
`;

const ImagePreview = styled.div`
  margin-top: 15px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;
  max-width: 300px;
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const ImageOverlay = styled.div`
  position: relative;
  cursor: pointer;
  
  &:hover::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 24px;
  }
`;

const ReplyForm = styled.form`
  padding: 20px;
  border-top: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ReplyTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 15px;
  color: #ffffff;
  font-size: 14px;
  resize: vertical;
  
  &::placeholder {
    color: #6c6c8a;
  }
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TicketInfo = styled(Card)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
`;

const TicketInfoHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const TicketInfoTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const TicketInfoContent = styled.div`
  padding: 20px;
`;

const InfoItem = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.div`
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
`;

const InfoValue = styled.div`
  color: #ffffff;
  font-size: 16px;
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'open': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      case 'in_progress': return 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)';
      case 'closed': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
`;

const PriorityBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.priority) {
      case 'low': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'medium': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      case 'high': return 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
`;

const ManagementCard = styled(Card)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
`;

const ManagementHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ManagementTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const ManagementContent = styled.div`
  padding: 20px;
`;

const Select = styled.select`
  width: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 10px 15px;
  color: #ffffff;
  font-size: 14px;
  margin-bottom: 15px;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
  
  option {
    background: #1a1a2e;
    color: #ffffff;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
`;

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    if (id) {
      fetchTicket();
    }
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.replies]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await ticketService.getTicketById(id);
      setTicket(data);
    } catch (err) {
      setError(err.message || 'خطا در بارگذاری تیکت');
      console.error('Error fetching ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    try {
      setSending(true);
      setError('');
      await ticketService.replyToTicket(id, { message: replyMessage });
      setReplyMessage('');
      setSuccess('پاسخ با موفقیت ارسال شد');
      await fetchTicket();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'خطا در ارسال پاسخ');
      console.error('Error sending reply:', err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      await ticketService.updateTicketStatus(id, newStatus);
      setSuccess(`وضعیت تیکت به ${getStatusText(newStatus)} تغییر یافت`);
      await fetchTicket();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'خطا در تغییر وضعیت');
      console.error('Error updating status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      setLoading(true);
      await ticketService.updateTicketPriority(id, newPriority);
      setSuccess(`اولویت تیکت به ${getPriorityText(newPriority)} تغییر یافت`);
      await fetchTicket();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'خطا در تغییر اولویت');
      console.error('Error updating priority:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    if (typeof status === 'number') {
      return TicketStatusPersian[status] || `نامشخص (${status})`;
    }
    
    const stringToEnum = {
      open: TicketStatus.Open,
      in_progress: TicketStatus.InProgress,
      closed: TicketStatus.Closed,
    };
    const enumValue = stringToEnum[status];
    return enumValue !== undefined ? TicketStatusPersian[enumValue] : status;
  };

  const getPriorityText = (priority) => {
    if (typeof priority === 'number') {
      return TicketPriorityPersian[priority] || `نامشخص (${priority})`;
    }
    
    const stringToEnum = {
      low: TicketPriority.Low,
      medium: TicketPriority.Medium,
      high: TicketPriority.High,
    };
    const enumValue = stringToEnum[priority];
    return enumValue !== undefined ? TicketPriorityPersian[enumValue] : priority;
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return 'تاریخ نامعتبر';
      }
      return date.toLocaleDateString('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (dateError) {
      console.error('Error formatting date:', dateError);
      return 'تاریخ نامعتبر';
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <File size={16} />;
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
      return <Image size={16} />;
    }
    
    return <File size={16} />;
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension);
  };

  const getFileName = (attachmentUrl) => {
    if (!attachmentUrl) return '';
    return attachmentUrl.split('/').pop() || 'فایل';
  };

  const handleDownloadAttachment = (attachmentUrl) => {
    if (!attachmentUrl) return;
    
    const fullUrl = getFullImageUrl(attachmentUrl);
    const fileName = getFileName(attachmentUrl);
    
    // Create a temporary link to download the file
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewImage = (attachmentUrl) => {
    if (!attachmentUrl) return;
    
    const fullUrl = getFullImageUrl(attachmentUrl);
    window.open(fullUrl, '_blank');
  };

  if (loading && !ticket) {
    return (
      <TicketDetailContainer>
        <LoadingSpinner>در حال بارگذاری تیکت...</LoadingSpinner>
      </TicketDetailContainer>
    );
  }

  if (error && !ticket) {
    return (
      <TicketDetailContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#ff6b6b' }}>
          {error}
        </div>
      </TicketDetailContainer>
    );
  }

  if (!ticket) {
    return (
      <TicketDetailContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#6c6c8a' }}>
          تیکت یافت نشد
        </div>
      </TicketDetailContainer>
    );
  }

  return (
    <TicketDetailContainer>
      <Header>
        <BackButton onClick={() => navigate('/support-tickets')}>
          <ArrowLeft size={16} />
          بازگشت به لیست تیکت‌ها
        </BackButton>
        <Title>تیکت #{ticket.id}</Title>
        <ActionButtons>
          {ticket.status !== 'closed' && (
            <Button 
              variant="danger" 
              onClick={() => handleStatusChange(TicketStatus.Closed)}
              disabled={loading}
              icon={<XCircle size={16} />}
            >
              بستن تیکت
            </Button>
          )}
        </ActionButtons>
      </Header>

      {error && (
        <div style={{ 
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)', 
          color: 'white', 
          padding: '15px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ 
          background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)', 
          color: 'white', 
          padding: '15px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          {success}
        </div>
      )}

      <MainContent>
        <ConversationArea>
          <ConversationHeader>
            <ConversationTitle>
              <MessageSquare size={20} />
              مکالمه
            </ConversationTitle>
          </ConversationHeader>
          
          <MessagesContainer>
            {/* Initial Message */}
            <MessageContainer isAdmin={false}>
              <Message isAdmin={false}>
                <MessageHeader>
                  <MessageAuthor>{ticket.playerUsername || 'نامشخص'}</MessageAuthor>
                  <MessageTime>{formatDate(ticket.createdAt)}</MessageTime>
                </MessageHeader>
                <MessageContent>
                  <h6>{ticket.title || 'بدون عنوان'}</h6>
                  <p>{ticket.message || 'بدون محتوا'}</p>
                </MessageContent>
              </Message>
            </MessageContainer>

            {/* Replies */}
            {Array.isArray(ticket.replies) &&
              ticket.replies.map((reply, index) => (
                <MessageContainer key={index} isAdmin={reply.authorType === 1}>
                  <Message isAdmin={reply.authorType === 1}>
                    <MessageHeader>
                      <MessageAuthor>{reply.authorName || 'نامشخص'}</MessageAuthor>
                      <MessageTime>{formatDate(reply.createdAt)}</MessageTime>
                    </MessageHeader>
                    <MessageContent>
                      <p>{reply.message || 'بدون محتوا'}</p>
                      
                      {/* Attachment Display */}
                      {reply.attachmentUrl && (
                        <>
                          {isImageFile(reply.attachmentUrl) ? (
                            <ImagePreview>
                              <ImageOverlay onClick={() => handleViewImage(reply.attachmentUrl)}>
                                <img 
                                  src={getFullImageUrl(reply.attachmentUrl)} 
                                  alt={getFileName(reply.attachmentUrl)}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div style={{ 
                                  display: 'none', 
                                  padding: '20px', 
                                  textAlign: 'center', 
                                  color: '#6c757d',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '10px'
                                }}>
                                  <File size={32} />
                                  <span>خطا در بارگذاری تصویر</span>
                                </div>
                              </ImageOverlay>
                            </ImagePreview>
                          ) : (
                            <AttachmentContainer>
                              <AttachmentIcon>
                                {getFileIcon(reply.attachmentUrl)}
                              </AttachmentIcon>
                              <AttachmentInfo>
                                <AttachmentName>{getFileName(reply.attachmentUrl)}</AttachmentName>
                                <AttachmentSize>فایل پیوست</AttachmentSize>
                              </AttachmentInfo>
                              <AttachmentActions>
                                <AttachmentButton onClick={() => handleDownloadAttachment(reply.attachmentUrl)}>
                                  <Download size={14} />
                                  دانلود
                                </AttachmentButton>
                                <AttachmentButton onClick={() => window.open(getFullImageUrl(reply.attachmentUrl), '_blank')}>
                                  <File size={14} />
                                  مشاهده
                                </AttachmentButton>
                              </AttachmentActions>
                            </AttachmentContainer>
                          )}
                        </>
                      )}
                    </MessageContent>
                  </Message>
                </MessageContainer>
              ))}

            <div ref={messagesEndRef} />
          </MessagesContainer>

          {/* Reply Form */}
          {ticket.status !== 'closed' && (
            <ReplyForm onSubmit={handleReplySubmit}>
              <ReplyTextarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="پاسخ خود را بنویسید..."
                disabled={sending}
              />
              <div style={{ marginTop: '15px', textAlign: 'left' }}>
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={sending || !replyMessage.trim()}
                  icon={<Send size={16} />}
                >
                  {sending ? 'در حال ارسال...' : 'ارسال پاسخ'}
                </Button>
              </div>
            </ReplyForm>
          )}
        </ConversationArea>

        <Sidebar>
          <TicketInfo>
            <TicketInfoHeader>
              <TicketInfoTitle>جزئیات تیکت</TicketInfoTitle>
            </TicketInfoHeader>
            <TicketInfoContent>
              <InfoItem>
                <InfoLabel>بازیکن</InfoLabel>
                <InfoValue>{ticket.playerUsername || 'نامشخص'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>دسته‌بندی</InfoLabel>
                <InfoValue>{ticket.category || 'عمومی'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>تاریخ ایجاد</InfoLabel>
                <InfoValue>{formatDate(ticket.createdAt)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>آخرین بروزرسانی</InfoLabel>
                <InfoValue>{formatDate(ticket.updatedAt)}</InfoValue>
              </InfoItem>
            </TicketInfoContent>
          </TicketInfo>

          <ManagementCard>
            <ManagementHeader>
              <ManagementTitle>مدیریت تیکت</ManagementTitle>
            </ManagementHeader>
            <ManagementContent>
              <InfoItem>
                <InfoLabel>وضعیت</InfoLabel>
                <Select 
                  value={typeof ticket.status === 'number' ? ticket.status : 
                    ticket.status === 'open' ? TicketStatus.Open :
                    ticket.status === 'in_progress' ? TicketStatus.InProgress :
                    ticket.status === 'closed' ? TicketStatus.Closed : TicketStatus.Open
                  } 
                  onChange={(e) => handleStatusChange(Number(e.target.value))} 
                  disabled={loading}
                >
                  <option value={TicketStatus.Open}>{TicketStatusPersian[TicketStatus.Open]}</option>
                  <option value={TicketStatus.InProgress}>{TicketStatusPersian[TicketStatus.InProgress]}</option>
                  <option value={TicketStatus.Closed}>{TicketStatusPersian[TicketStatus.Closed]}</option>
                </Select>
              </InfoItem>

              <InfoItem>
                <InfoLabel>اولویت</InfoLabel>
                <Select
                  value={typeof ticket.priority === 'number' ? ticket.priority :
                    ticket.priority === 'low' ? TicketPriority.Low :
                    ticket.priority === 'medium' ? TicketPriority.Medium :
                    ticket.priority === 'high' ? TicketPriority.High : TicketPriority.Medium
                  }
                  onChange={(e) => handlePriorityChange(Number(e.target.value))}
                  disabled={loading}
                >
                  <option value={TicketPriority.Low}>{TicketPriorityPersian[TicketPriority.Low]}</option>
                  <option value={TicketPriority.Medium}>{TicketPriorityPersian[TicketPriority.Medium]}</option>
                  <option value={TicketPriority.High}>{TicketPriorityPersian[TicketPriority.High]}</option>
                </Select>
              </InfoItem>

              <InfoItem>
                <InfoLabel>وضعیت فعلی</InfoLabel>
                <div style={{ marginTop: '5px' }}>
                  <StatusBadge status={ticket.status}>
                    {getStatusText(ticket.status)}
                  </StatusBadge>
                </div>
              </InfoItem>

              <InfoItem>
                <InfoLabel>اولویت فعلی</InfoLabel>
                <div style={{ marginTop: '5px' }}>
                  <PriorityBadge priority={ticket.priority}>
                    {getPriorityText(ticket.priority)}
                  </PriorityBadge>
                </div>
              </InfoItem>
            </ManagementContent>
          </ManagementCard>
        </Sidebar>
      </MainContent>
    </TicketDetailContainer>
  );
};

export default TicketDetail;
